import React from "react";
import { useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";

interface CenterManagedNoticeProps {
    /** A one-line badge for the catalogue card; the full explanation otherwise. */
    compact?: boolean;
    /**
     * What the centre has decided about this tool.
     *
     * Three answers, not two. "Switched off" and "never switched on" look the
     * same to a therapist — they do not have it either way — but they are
     * different things to say, and saying `enabled` for both of the others is
     * how a module the centre never bought came to read as "enabled by your
     * centre" on a card that then did nothing.
     */
    state?: "enabled" | "disabled" | "notEnabled";
}

/**
 * Says that a tool is the centre's call, not the therapist's.
 *
 * Shown where the adopt button would otherwise invite a click the server is
 * going to refuse. Naming who decided — and that nothing the therapist set up
 * is lost — is what turns a dead button into something they can act on: ask
 * whoever runs the centre.
 */
const CenterManagedNotice: React.FC<CenterManagedNoticeProps> = ({
    compact = false,
    state = "notEnabled",
}) => {
    const { t } = useTranslation();
    const enabled = state === "enabled";

    if (compact) {
        return (
            <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                    enabled
                        ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
                        : "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300"
                }`}
            >
                <Building2 size={12} aria-hidden="true" />
                {t(`marketplace.center.${state}.badge`)}
            </span>
        );
    }

    return (
        <div
            role="note"
            className="mt-6 flex gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-white/[0.03]"
        >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-gray-600 dark:bg-white/5 dark:text-gray-300">
                <Building2 size={18} aria-hidden="true" />
            </span>
            <div>
                <p className="text-sm font-semibold text-content-primary">
                    {t(`marketplace.center.${state}.title`)}
                </p>
                <p className="mt-1 text-sm text-content-tertiary">
                    {t(`marketplace.center.${state}.body`)}
                </p>
            </div>
        </div>
    );
};

export default CenterManagedNotice;
